import { useState, useEffect } from 'react';

/**
 * Analyzes an image's brightness to determine if it needs a light or dark background
 * @param {string} imageUrl - URL of the image to analyze
 * @returns {string} - 'light' or 'dark' background recommendation
 */
const useLogoBackground = (imageUrl) => {
  const [backgroundType, setBackgroundType] = useState('light'); // default to light

  useEffect(() => {
    if (!imageUrl) {
      setBackgroundType('light');
      return;
    }

    const analyzeImage = () => {
      const img = new Image();
      img.crossOrigin = 'anonymous'; // Handle CORS if needed

      img.onload = () => {
        try {
          // Create a canvas to analyze the image
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Set canvas size (we'll sample a smaller size for performance)
          const sampleSize = 100;
          canvas.width = sampleSize;
          canvas.height = sampleSize;
          
          // Draw the image to canvas
          ctx.drawImage(img, 0, 0, sampleSize, sampleSize);
          
          // Get image data
          const imageData = ctx.getImageData(0, 0, sampleSize, sampleSize);
          const data = imageData.data;
          
          // Calculate average brightness
          let totalBrightness = 0;
          let pixelCount = 0;
          
          // Sample every 4th pixel for performance (RGBA format)
          for (let i = 0; i < data.length; i += 16) {
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            const a = data[i + 3];
            
            // Only count non-transparent pixels
            if (a > 0) {
              // Calculate brightness using luminance formula
              // Y = 0.299*R + 0.587*G + 0.114*B
              const brightness = 0.299 * r + 0.587 * g + 0.114 * b;
              totalBrightness += brightness;
              pixelCount++;
            }
          }
          
          if (pixelCount > 0) {
            const averageBrightness = totalBrightness / pixelCount;
            // If average brightness is above 128 (midpoint of 0-255), use dark background
            // If below, use light background
            setBackgroundType(averageBrightness > 128 ? 'dark' : 'light');
          } else {
            setBackgroundType('light'); // fallback
          }
        } catch (error) {
          console.warn('Error analyzing logo brightness:', error);
          setBackgroundType('light'); // fallback to light
        }
      };

      img.onerror = () => {
        setBackgroundType('light'); // fallback to light on error
      };

      img.src = imageUrl;
    };

    analyzeImage();
  }, [imageUrl]);

  return backgroundType;
};

export default useLogoBackground;

